import React, { ComponentProps } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useToast } from 'contexts/ToastContext'
import { usePostResetPasswordEmail } from 'features/Login/api/mutations/usePostResetPasswordEmail'
import { Icon } from 'components/Icon'
import { Link } from 'components/Link'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { Button } from 'components/Button'
import { SentInstructionsRoot, Description, ResendButtonWrapper } from './SentInstructions.styles'

type SentInstructionsOwnProps = ComponentProps<typeof SentInstructionsRoot>
export type SentInstructionsProps = SentInstructionsOwnProps & {
  email: string
}

export const SentInstructions = ({ email }: SentInstructionsProps) => {
  const { t } = useTranslation()

  const { postResetPasswordEmail } = usePostResetPasswordEmail()

  const { addToast, removeToast } = useToast()

  const resendEmail = () => {
    postResetPasswordEmail(
      { email },
      {
        onSuccess: () => {
          addToast({
            title: t('login:toast.email.success.title'),
            message: t('login:toast.email.success.message'),
            duration: 5000,
            onClose: () => removeToast('error-password'),
            tone: 'success',
            id: 'success-password-resend',
          })
        },
        onError: () => {
          addToast({
            message: 'The email could not be sent.',
            duration: 5000,
            onClose: () => removeToast('error-password'),
            tone: 'error',
            id: 'error-password-resend',
          })
        },
      }
    )
  }

  return (
    <SentInstructionsRoot direction="column" justifyContent="center" gap="5">
      <Description>
        <Stack direction="column" gap={{ '@initial': '3', '@bp2': '2' }}>
          <Stack direction="column" gap={{ '@initial': '1', '@bp2': '2' }}>
            <Icon
              name="envelope-open"
              size={{ '@initial': '5', '@bp2': '7' }}
              color="accent-black"
            />
            <Text
              size={{ '@initial': 'h4', '@bp2': 'h3' }}
              as="h3"
              weight="bold"
              color="accent-black"
            >
              {t('reset-password:mail')}
            </Text>
          </Stack>
          <Text color="accent-black">{t('reset-password:mail-description')}</Text>
        </Stack>
        <Text color="accent-black">{t('reset-password:not-received')}</Text>
      </Description>
      <ResendButtonWrapper>
        <Button onClick={resendEmail}>{t('reset-password:resend')}</Button>
        <Link href="/" passHref>
          <Text as="a" color="accent-black" align="center">
            {t('reset-password:skip')}
          </Text>
        </Link>
      </ResendButtonWrapper>
    </SentInstructionsRoot>
  )
}

SentInstructions.displayName = 'SentInstructions'
